/**
 * Single source of truth for "what is each card doing right now."
 *
 * Owns:
 *   - The visible deck order (engine order, overridden by skip-reinsertion).
 *   - A per-card status overlay (idle, swipe-pending, exit-skip, exit-complete).
 *
 * Consumers call high-level intents (`skip`, `complete`, `beginSwipePending`,
 * `resolveSwipe`) and the controller updates state. The renderer reads the
 * resulting `cards` array and animates per-card status via `deckAnimate`.
 *
 * Animation completion is signalled back via `onExitAnimationComplete` from
 * the card wrapper's `onAnimationComplete` — no setTimeouts, no parallel
 * `animate()` timers.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CardStatus } from "@/web/routes/_protected/smb/-dashboard/-deckAnimate";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";

export type DeckCard = {
  readonly id: string;
  readonly status: CardStatus;
  readonly definition: SmbActionDefinition;
};

export type CardDeckController = {
  readonly cards: readonly DeckCard[];
  /**
   * 1-based position of the front card within the current pass through the
   * deck, for the "3 of 7" cue under the stack.
   *
   * The deck is a loop — skipping recycles a card to the back rather than
   * consuming it — so a plain "N remaining" counter would sit motionless while
   * the user swiped, which reads as broken. Instead the *numerator* advances on
   * each skip and wraps back to 1 once the user has cycled through everything
   * (that wrap is the "you've seen them all, here's the top again" signal),
   * while the *denominator* only drops when a card is actually answered. That
   * split is deliberate: skipping is not progress, and the number shouldn't
   * pretend otherwise.
   */
  readonly passPosition: number;
  /**
   * The "of N" denominator: cards still in the deck, counting ones mid-skip
   * (they're being recycled to the back, not removed) but not ones mid-
   * completion (they're on their way out for good).
   */
  readonly liveCount: number;
  skip: (id: string) => void;
  complete: (id: string) => void;
  /**
   * Like `complete`, but recycles the card to the back of the deck instead of
   * hiding it — same exit + reinsertion path as `skip`. For actions whose work
   * is repeatable and that should stay in the active deck after finishing
   * (e.g. "share business card"), rather than being dismissed.
   */
  recycle: (id: string) => void;
  beginSwipePending: (id: string, direction: "left" | "right") => void;
  resolveSwipe: (id: string, outcome: "committed" | "rejected") => void;
  onExitAnimationComplete: (id: string) => void;
};

export function useCardDeck(actions: readonly SmbActionDefinition[]): CardDeckController {
  // In-flight status overlay. Cards not present here are implicitly `idle`.
  const [statusById, setStatusById] = useState<ReadonlyMap<string, CardStatus>>(new Map());
  // Skip-reinsertion order — ids appended at end in the order they were skipped.
  const [skipOrder, setSkipOrder] = useState<readonly string[]>([]);
  // Ids whose exit-complete animation has finished. Kept hidden until the
  // engine drops them from `actions`, which avoids a flash of the un-faded
  // card while the server round-trip catches up.
  const [hiddenIds, setHiddenIds] = useState<readonly string[]>([]);
  // Skips taken in the current pass through the deck. Wraps to 0 once it
  // reaches the live card count, and resets whenever a card is answered (a
  // completion re-tops the deck, so the old position is meaningless).
  const [skipsThisPass, setSkipsThisPass] = useState(0);

  // Snapshots of definitions for in-flight cards. If the engine drops a card
  // mid-animation we keep rendering it from this snapshot until the exit
  // animation completes.
  const orphanDefsRef = useRef<Map<string, SmbActionDefinition>>(new Map());

  // Mirror of `statusById` updated synchronously each render. Lets callbacks
  // like `onExitAnimationComplete` read the current status without nesting
  // a `setState` inside another `setState`'s updater (which would violate
  // React's purity contract — StrictMode runs updaters twice and would
  // double-fire any nested `setState` calls).
  const statusByIdRef = useRef<ReadonlyMap<string, CardStatus>>(statusById);
  statusByIdRef.current = statusById;

  // Reconcile against engine output on every change to `actions`.
  useEffect(() => {
    const liveIds = new Set(actions.map((a) => a.id));

    setStatusById((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, status] of prev) {
        if (!liveIds.has(id) && status.kind === "idle") {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setHiddenIds((prev) => {
      const filtered = prev.filter((id) => liveIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });

    setSkipOrder((prev) => {
      const filtered = prev.filter((id) => liveIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });

    // Drop orphan snapshots for ids that came back into the engine — the
    // engine's definition wins.
    for (const id of orphanDefsRef.current.keys()) {
      if (liveIds.has(id)) {
        orphanDefsRef.current.delete(id);
      }
    }
  }, [actions]);

  const beforeMutateSnapshot = useCallback(
    (id: string) => {
      // If this id is currently in engine actions, stash its definition so we
      // can keep rendering through an exit animation even if the engine drops
      // it mid-animation.
      const def = actions.find((a) => a.id === id);
      if (def != null && !orphanDefsRef.current.has(id)) {
        orphanDefsRef.current.set(id, def);
      }
    },
    [actions],
  );

  const setStatus = useCallback((id: string, status: CardStatus | null) => {
    setStatusById((prev) => {
      const next = new Map(prev);
      if (status == null) {
        next.delete(id);
      } else {
        next.set(id, status);
      }
      return next;
    });
  }, []);

  const skip = useCallback(
    (id: string) => {
      beforeMutateSnapshot(id);
      setStatus(id, { kind: "exit-skip" });
    },
    [beforeMutateSnapshot, setStatus],
  );

  const complete = useCallback(
    (id: string) => {
      beforeMutateSnapshot(id);
      setStatus(id, { kind: "exit-complete" });
    },
    [beforeMutateSnapshot, setStatus],
  );

  // Recycle reuses the `exit-skip` status so the card peels out and gets
  // re-appended to `skipOrder` on settle (see `onExitAnimationComplete`),
  // landing at the back of the deck instead of being hidden.
  const recycle = useCallback(
    (id: string) => {
      beforeMutateSnapshot(id);
      setStatus(id, { kind: "exit-skip" });
    },
    [beforeMutateSnapshot, setStatus],
  );

  const beginSwipePending = useCallback(
    (id: string, direction: "left" | "right") => {
      beforeMutateSnapshot(id);
      setStatus(id, { kind: "swipe-pending", direction });
    },
    [beforeMutateSnapshot, setStatus],
  );

  const resolveSwipe = useCallback(
    (id: string, outcome: "committed" | "rejected") => {
      if (outcome === "committed") {
        setStatus(id, { kind: "exit-complete" });
      } else {
        setStatus(id, null);
        orphanDefsRef.current.delete(id);
      }
    },
    [setStatus],
  );

  const onExitAnimationComplete = useCallback((id: string) => {
    // Read current status from the synchronously-mirrored ref, then dispatch
    // each piece of state at the top level — no setState calls nested inside
    // another setState's updater.
    const status = statusByIdRef.current.get(id);
    if (status == null) return;

    if (status.kind === "exit-skip") {
      setStatusById((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      // Always move to the end of the skip order — once every card has been
      // skipped once, re-skipping the front card needs to push it behind the
      // others. A short-circuit on `includes` would leave the card at its
      // old position and the deck would stop cycling.
      setSkipOrder((order) => [...order.filter((x) => x !== id), id]);
      // Counted unbounded; the wrap is applied as a modulo when deriving
      // `passPosition`, so this never needs to know the live card count.
      setSkipsThisPass((n) => n + 1);
      orphanDefsRef.current.delete(id);
      return;
    }

    if (status.kind === "exit-complete") {
      setStatusById((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setHiddenIds((hidden) => (hidden.includes(id) ? hidden : [...hidden, id]));
      // A completion re-tops the deck, so the position within the old pass no
      // longer means anything.
      setSkipsThisPass(0);
      // Keep the orphan snapshot until the engine confirms removal — once
      // hidden, the card no longer renders, so the snapshot's only purpose
      // is settled. Drop it.
      orphanDefsRef.current.delete(id);
    }
  }, []);

  const cards = useMemo<readonly DeckCard[]>(() => {
    const live = actions.filter((a) => !hiddenIds.includes(a.id));
    const fromEngine = new Map(live.map((a) => [a.id, a]));

    // Include orphan in-flight cards the engine has already dropped, so their
    // exit animation can finish gracefully.
    const orphans: SmbActionDefinition[] = [];
    for (const [id, def] of orphanDefsRef.current) {
      if (!fromEngine.has(id) && !hiddenIds.includes(id)) {
        orphans.push(def);
      }
    }

    const pool: SmbActionDefinition[] = [...live, ...orphans];

    // Stable sort: ids in skipOrder go to the end in their skip order; the
    // rest preserve engine order.
    const skipIndex = new Map(skipOrder.map((id, i) => [id, i]));
    pool.sort((a, b) => {
      const ai = skipIndex.get(a.id) ?? -1;
      const bi = skipIndex.get(b.id) ?? -1;
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return -1;
      if (bi === -1) return 1;
      return ai - bi;
    });

    return pool.map((definition) => ({
      id: definition.id,
      definition,
      status: statusById.get(definition.id) ?? { kind: "idle" },
    }));
  }, [actions, hiddenIds, skipOrder, statusById]);

  // Only `exit-complete` leaves the deck. `exit-skip` is a *recycle* — the card
  // peels out and lands at the back — so it must keep counting, or the
  // denominator dips for the length of the peel and pops back.
  //
  // That dip isn't merely cosmetic: `passPosition` takes `skipsThisPass` modulo
  // this number, so a denominator that changes mid-animation changes the
  // modulo base too and the whole fraction jumps (5 of 5 → 1 of 4 → 5 of 5).
  // Exclude only what is genuinely going away.
  const liveCount = cards.reduce((n, c) => n + (c.status.kind === "exit-complete" ? 0 : 1), 0);
  const passPosition = liveCount === 0 ? 0 : (skipsThisPass % liveCount) + 1;

  return {
    cards,
    passPosition,
    liveCount,
    skip,
    complete,
    recycle,
    beginSwipePending,
    resolveSwipe,
    onExitAnimationComplete,
  };
}
